import {
    AccountInfo,
    InteractionRequiredAuthError,
    LogLevel,
    PublicClientApplication
} from '@azure/msal-browser';

import apiClient from '@/services/api_client';
import { useChatSessionsStore } from '@/stores/chatSessions';
/**
 * Check if we're currently in a redirect callback by examining the URL
 * Only detect Microsoft OAuth callbacks, not other auth providers (like Descope)
 */
export function isRedirectCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;

    // Ignore Descope callbacks
    if (urlParams.has('descope-login-flow')) {
        return false;
    }

    // Check for Microsoft OAuth parameters
    // Microsoft uses 'state' parameter in addition to 'code'
    // or error parameters specific to OAuth
    const hasCode = urlParams.has('code');
    const hasState = urlParams.has('state');
    const hasError = urlParams.has('error');
    const hasErrorDescription = urlParams.has('error_description');

    // Microsoft callback will have either:
    // 1. code + state (success)
    // 2. error + error_description (failure)
    // 3. tokens in hash (implicit flow)
    return (
        (hasCode && hasState) ||
        (hasError && hasErrorDescription) ||
        hash.includes('access_token') ||
        hash.includes('id_token')
    );
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
async function getMsalConfig(clientId: string, azureTenantId: string) {
    return {
        auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${azureTenantId}`,
            redirectUri: `${window.location.origin}/`,
            postLogoutRedirectUri: `${window.location.origin}/`
        },
        cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: true
        },
        system: {
            loggerOptions: {
                loggerCallback: (
                    level: LogLevel,
                    message: string,
                    containsPii: boolean
                ) => {
                    if (containsPii) return;

                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                    }
                }
            }
        }
    };
}

/**
 * The scopes without which there is no product. They are in the sign-in request, so a
 * missing consent here is a blocked sign-in -- nothing optional may ever join them.
 */
function getScopes() {
    return ['User.Read', 'Sites.Read.All'];
}

async function selectAccount(
    msalApp: PublicClientApplication,
    userEmail: string
) {
    const scopes = getScopes();

    // First, check if there are existing accounts
    const accounts = msalApp.getAllAccounts();
    if (accounts.length > 0) {
        try {
            const response = await msalApp.acquireTokenSilent({
                scopes,
                account: accounts[0]
            });
            return response.account;
        } catch (error) {
            console.log(
                'Silent token acquisition failed, will try interactive login'
            );
        }
    }

    // Try SSO Silent if we have a login hint
    if (userEmail) {
        try {
            const loginResponse = await msalApp.ssoSilent({
                scopes,
                loginHint: userEmail
            });
            return loginResponse.account;
        } catch (err) {
            console.log('SSO Silent failed, will try interactive login');
        }
    }

    // Only redirect if we're not already in a callback and not in an iframe
    if (!isRedirectCallback() && window.self === window.top) {
        console.log('Starting interactive login flow');
        msalApp.loginRedirect({ scopes, loginHint: userEmail });
        return undefined;
    }

    return undefined;
}

async function getMsalApp(clientId: string, azureTenantId: string) {
    const msalConfig = await getMsalConfig(clientId, azureTenantId);
    const msalApp = new PublicClientApplication(msalConfig);
    await msalApp.initialize();
    return msalApp;
}

export async function handleMicrosoftConnection(
    clientId: string,
    azureTenantId: string,
    userEmail: string,
    microsoftRedirectHash: string
) {
    const msalApp = await getMsalApp(clientId, azureTenantId);

    // Always handle redirect promise first
    const response = await msalApp.handleRedirectPromise(
        microsoftRedirectHash || undefined
    );

    if (response !== null) {
        console.log('Login successful via redirect');
        return response.account;
    }

    // Check if we have existing accounts
    const currentAccounts = msalApp.getAllAccounts();
    if (currentAccounts.length > 0) {
        console.log('Found existing account');
        return currentAccounts[0];
    }

    // Only proceed with login if we're not in a redirect callback
    if (!isRedirectCallback()) {
        return await selectAccount(msalApp, userEmail);
    }

    return undefined;
}

export type MicrosoftToken = {
    accessToken: string | undefined;
    /** What Microsoft actually granted, which is not always what was asked for. */
    scopes: string[];
};

/**
 * Gets a Graph token for this account, along with the actual granted scopes.
 *
 * Microsoft returns all delegated scopes ever consented for the user, not just the ones requested.
 * We never ask for optional scopes. Missing/revoked permissions don't trigger a consent prompt;
 * they're simply absent from `scopes`.
 *
 * Pass `forceRefresh` after a consent screen, so the token reflects the new grant.
 */
export async function getMicrosoftToken(
    account: AccountInfo,
    clientId: string,
    azureTenantId: string,
    forceRefresh = false
): Promise<MicrosoftToken | undefined> {
    const msalApp = await getMsalApp(clientId, azureTenantId);
    const scopes = getScopes();

    try {
        const tokenResponse = await msalApp.acquireTokenSilent({
            account,
            scopes,
            forceRefresh
        });
        return {
            accessToken: tokenResponse.accessToken,
            scopes: tokenResponse.scopes
        };
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            const chatSessionStore = useChatSessionsStore();
            const currentMessage = chatSessionStore.currentMessage;
            sessionStorage.setItem('chatMessage', currentMessage);

            await msalApp.acquireTokenRedirect({
                account,
                scopes
            });
        }
    }
}

export async function logoutMicrosoft(clientId: string, azureTenantId: string) {
    const msalApp = await getMsalApp(clientId, azureTenantId);
    msalApp.logoutRedirect();
}

export async function createSharePointCredentials(
    tenantId: string,
    azureTenantId: string,
    microsoftAppId: string,
    microsoftAppPassword: string
) {
    const url = '/sharepoint/credentials';
    const body = {
        tenant_id: tenantId,
        azure_tenant_id: azureTenantId,
        microsoft_app_id: microsoftAppId,
        microsoft_app_password: microsoftAppPassword
    };
    return await apiClient.post(url, body);
}
